class AllProductsGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]');
    this.viewMoreTrigger = this.querySelector('[data-all-products-grid-view-more]');
    this.collectionUrl = this.dataset.collectionUrl;
    this.sectionRenderId = this.dataset.sectionRenderId || '';
    this.instanceId = this.dataset.instanceId || 'all-products-grid';
    this.nextPage = Number(this.dataset.nextPage || '2');
    this.loading = false;
    this.seenProductIds = new Set(
      Array.from(this.querySelectorAll('[ref="cards[]"][data-product-id]')).map((item) => item.getAttribute('data-product-id'))
    );

    if (!this.grid || !this.viewMoreTrigger || !this.collectionUrl) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadNextPage();
          }
        }
      },
      {
        rootMargin: '300px',
      }
    );

    this.observer.observe(this.viewMoreTrigger);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async loadNextPage() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      const sectionRenderId = await this.resolveSectionRenderId();

      if (!sectionRenderId) {
        this.finishLoading();
        return;
      }

      const requestedPage = this.nextPage;
      const url = new URL(this.collectionUrl, window.location.origin);
      url.searchParams.set('section_id', sectionRenderId);
      url.searchParams.set('page', String(requestedPage));

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load page ${this.nextPage}`);
      }

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = documentFragment.querySelector('[ref="grid"]');

      if (!nextGrid) {
        this.finishLoading();
        return;
      }

      const items = Array.from(nextGrid.querySelectorAll(':scope > [ref="cards[]"]'));
      const responseLastPage = Number(nextGrid.getAttribute('data-last-page') || '1');

      if (items.length === 0) {
        this.finishLoading();
        return;
      }

      for (const item of items) {
        const productId = item.getAttribute('data-product-id');

        if (productId && this.seenProductIds.has(productId)) {
          continue;
        }

        if (productId) {
          this.seenProductIds.add(productId);
          item.id = `${this.instanceId}-${productId}`;
        } else {
          item.removeAttribute('id');
        }

        item.setAttribute('data-page', String(requestedPage));
        this.grid.appendChild(item);
      }

      this.nextPage = requestedPage + 1;
      this.dataset.nextPage = String(this.nextPage);

      if (requestedPage >= responseLastPage) {
        this.finishLoading();
      }
    } catch (_error) {
      this.finishLoading();
    } finally {
      this.loading = false;
    }
  }

  async resolveSectionRenderId() {
    if (this.sectionRenderId && this.sectionRenderId.includes('__main')) {
      return this.sectionRenderId;
    }

    const url = new URL(this.collectionUrl, window.location.origin);
    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      return null;
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const mainSection = documentFragment.querySelector('.shopify-section[id*="__main"]');

    if (!(mainSection instanceof HTMLElement)) {
      return null;
    }

    this.sectionRenderId = mainSection.id.replace(/^shopify-section-/, '');
    this.dataset.sectionRenderId = this.sectionRenderId;

    return this.sectionRenderId;
  }

  finishLoading() {
    this.observer?.disconnect();
    this.viewMoreTrigger?.remove();
  }
}

if (!customElements.get('all-products-grid')) {
  customElements.define('all-products-grid', AllProductsGrid);
}
